import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useSubscription, Query } from 'react-apollo';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import { makeStyles } from '@material-ui/core/styles';
import Message from 'modules/student/chat/components/Message';
import Composer from 'modules/student/chat/components/Composer';
import Spin from 'components/common/Spin';
import {
  LISTEN_ROOM_MESSAGES,
  INSERT_MESSAGE,
  GET_ROOM,
  UPDATE_USER_ROOM_STATUS,
  UPSERT_CHATROOM_USERS,
} from 'graphql/chat';
import { Link } from 'react-router-dom';
import { getFullName, getInitials } from 'utils';
import orderBy from 'lodash/orderBy';
import { getRoomName } from './ListItem';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 130px)',
    overflow: 'hidden',
  },
  header: {
    borderBottom: '1px solid #eee',
  },
  avatar: {
    backgroundColor: '#eee',
    color: '#333',
  },
  messages: {
    overflowY: 'auto',
    flex: 1,
    backgroundColor: theme.palette.background['blueGray'],
  },
  link: {
    'color': 'inherit',
    'a&:visited': {
      color: 'inherit',
    },
  },
}));

const sortUsers = (users, roles) => {
  const sorted = [];
  users.forEach(({ user }) => {
    user.order = roles.indexOf(user.user_type);
    sorted.push(user);
  });
  // roles.forEach(role => {
  //   const user = users.find(i => i.user.user_type === role);
  //   if (user) {
  //     sorted.push(user.user);
  //   }
  // });
  return orderBy(sorted, 'order');
};

const RoomUsers = {
  coaches: ['student', 'lead_coach', 'head_coach', 'admin'],
  admin: ['student', 'admin'],
  parents_coaches: ['parent', 'head_coach', 'lead_coach', 'admin'],
  parents_admin: ['parent', 'admin'],
  groups: ['head_coach', 'lead_coach', 'student', 'admin'],
  parents_fa: ['parent', 'fa_coach']
};

const Conversation = ({
  match: {
    params: { roomId },
  },
}) => {
  const classes = useStyles();
  const listRef = useRef();
  const user = useSelector((s) => s.user.user);
  const [updateStatus] = useMutation(UPDATE_USER_ROOM_STATUS, {
    variables: { userId: user.id, roomId, timestamp:  "now()" },
  });
  const { data, loading } = useSubscription(LISTEN_ROOM_MESSAGES, {
    variables: { id: roomId },
  });
  const [addMessage] = useMutation(INSERT_MESSAGE);
  const [addUser] = useMutation(UPSERT_CHATROOM_USERS);
  const [users, setusers] = useState();

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [data, listRef.current]);

  useEffect(() => {
    if (data) {
      updateStatus();
    }
  }, [data]);

  const onSend = async (message, attachment) => {
    console.log({
      message,
      attachment,
      room_id: roomId,
      user_id: user.id,
    });
    return addMessage({
      variables: {
        data: {
          message,
          attachment,
          room_id: roomId,
          user_id: user.id,
        },
      },
    }).then(() => {
      if (
        user.type === 'admin' &&
        !users.find((f) => f.user.user_id == user.id)
      ) {
        addUser({
          variables: {
            data: [{ room_id: roomId, user_id: user.id }],
          },
        });
      }
    });
  };

  return (
    <Card className={classes.root}>
      <Query
        query={GET_ROOM}
        variables={{ id: roomId }}
        fetchPolicy="cache-and-network"
      >
        {({ data: roomData, error }) => {
          if (!roomData) return null;
          if (error) return error.message;

          const student = roomData.room.student;
          const group = roomData.room.group;
          const isGroup = roomData.room.room_name === 'groups';
          const sortedUsers = sortUsers(
            roomData.room.users,
            RoomUsers[roomData.room.room_name],
          );

          setusers(roomData.room.users);

          const mentions = roomData.room.users.map((i) => ({
            id: i.user.user_id,
            display: i.user.user_first_name,
          }));

          const roomName = getRoomName(
            {
              room: roomData.room,
              room_name: roomData.room.room_name,
            },
            user.type,
          );

          return (
            <>
              <CardHeader
                className={classes.header}
                avatar={
                  isGroup ? (
                    <Avatar className={classes.avatar}>
                      {getInitials(group?.name)}
                    </Avatar>
                  ) : (
                    <Link
                      className={classes.link}
                      to={`/students/${student.id}/timeline`}
                    >
                      <Avatar src={student.user_pic} className={classes.avatar}>
                        {getInitials(
                          student.user_first_name,
                          student.user_last_name,
                        )}
                      </Avatar>
                    </Link>
                  )
                }
                title={
                  isGroup ? (
                    group?.name
                  ) : (
                    <Link
                      className={classes.link}
                      to={`/students/${student.id}/timeline`}
                    >
                      {roomName}
                    </Link>
                  )
                }
                titleTypographyProps={{
                  color: 'inherit',
                  variant: 'h6',
                }}
                subheader={
                  <Box display="flex" alignItems="center">
                    <PeopleOutlineIcon fontSize="small" />
                    &nbsp;
                    <Typography variant="caption" color="textSecondary">
                      {sortedUsers.map((u) => getFullName(u)).join(', ')}
                    </Typography>
                  </Box>
                }
                subheaderTypographyProps={{
                  color: 'inherit',
                  variant: 'body2',
                }}
              />
              <CardContent className={classes.messages} ref={listRef}>
                {data?.messages.map((m) => (
                  <Message
                    user={user}
                    message={m}
                    userId={user.id}
                    key={m.id}
                  />
                ))}
                <Spin spinning={loading} linear />
              </CardContent>
              <Composer
                users={mentions}
                placeholder="Enter your message..."
                onSend={onSend}
              />
            </>
          );
        }}
      </Query>
    </Card>
  );
};

export default Conversation;
